/**
 * COPIED/ADAPTED from pages/dev-agent/components/metadataEditor/AcceptedCodeDiffSection.tsx.
 *
 * MULTI-AGENT difference: when the active file is a MULTI-AGENT proposed
 * artifact (i.e. an `originalArtifactId` / `metadata.source_artifact_id` is
 * known), the comparison is delegated to `MultiAgentCompareView`, which resolves
 * the ORIGINAL (left/baseline) lazily via `GET /api/artifacts/{id}` rather than
 * relying on `originalContentForDiff` text already in hand.
 *
 * When there is no `originalArtifactId` we render an inline Monaco DiffEditor
 * with a Cursor-style sticky review toolbar (hunk navigation, Accept/Reject
 * all, Apply/Close). Per-hunk Accept ✓ / Reject ✗ appear as small React overlay
 * buttons inside each spacer zone — they are never inside Monaco's DOM, so they
 * always receive click events reliably.
 */
import { useCallback, useEffect, useRef, type MutableRefObject } from "react";
import { createPortal } from "react-dom";
import type * as monaco from "monaco-editor";
import { DiffCodeEditor, type MonacoEditorTheme } from "@ncoder/code-editor";
import { ChevronUp, ChevronDown, Maximize2, X as XIcon } from "lucide-react";
import { getMonacoLanguage } from "./monacoLanguage";
import { MultiAgentCompareView } from "./MultiAgentCompareView";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import type { OpenFile } from "./types";
import type { Dispatch, SetStateAction } from "react";

/** Clear the pending-operation flag (acceptedCodeOperation/Target) whenever a
 *  diff review resolves — Accept, Reject, Apply, or Close. Without this, a
 *  REJECTED delete leaves isDeleteTarget stale in useMetadataEditorPanel, so a
 *  later plain Save on the (now-reverted) file would still delete it. */
function clearPendingOperation(activeFileId: string): void {
  const ide = useMultiAgentIdeStore.getState();
  if (ide.acceptedCodeTarget === activeFileId) {
    ide.setAcceptedCodeOperation(null);
    ide.setAcceptedCodeTarget(null);
  }
}

export function AcceptedCodeDiffSection({
  activeFileId,
  activeFile,
  acceptedCode,
  originalContentForDiff,
  originalArtifactId,
  editorTheme,
  diffEditorRef,
  diffOverlayOpen,
  setDiffOverlayOpen,
  diffHunkTotal,
  diffHunkResolved,
  setDiffHunkTotal,
  setDiffHunkResolved,
  setContentByFileId,
  setOriginalByFileId,
  setDirtyByFileId,
  onAcceptedCodeChange,
  handleSave,
}: {
  activeFileId: string;
  activeFile: OpenFile | null;
  acceptedCode: string;
  originalContentForDiff: string;
  /**
   * UUID of the source artifact this proposed change was derived from
   * (`metadata.source_artifact_id`). When set, the comparison resolves the
   * original via `GET /api/artifacts/{id}` through `MultiAgentCompareView`.
   * When null/undefined, the in-hand `originalContentForDiff` is used.
   */
  originalArtifactId?: string | null;
  editorTheme: MonacoEditorTheme;
  diffEditorRef: MutableRefObject<monaco.editor.IStandaloneDiffEditor | null>;
  diffOverlayOpen: boolean;
  setDiffOverlayOpen: Dispatch<SetStateAction<boolean>>;
  diffHunkTotal: number;
  diffHunkResolved: number;
  setDiffHunkTotal: Dispatch<SetStateAction<number>>;
  setDiffHunkResolved: Dispatch<SetStateAction<number>>;
  setContentByFileId: Dispatch<SetStateAction<Record<string, string>>>;
  setOriginalByFileId: Dispatch<SetStateAction<Record<string, string>>>;
  setDirtyByFileId: Dispatch<SetStateAction<Record<string, boolean>>>;
  onAcceptedCodeChange: (code: string | null) => void;
  handleSave: () => void;
}) {
  /** `onDidUpdateDiff` listener — dispose before editor teardown to avoid Monaco `Canceled` noise. */
  const diffHunkStatsListenerRef = useRef<monaco.IDisposable | null>(null);

  /** Imperative handle set by DiffCodeEditor so we can scroll to a specific hunk. */
  const scrollToHunkRef = useRef<((idx: number) => void) | null>(null);

  /** Tracks the currently focused hunk index for ← → navigation (no re-render needed). */
  const currentHunkIdxRef = useRef(0);

  // Reset navigation state when the file or proposed code changes.
  useEffect(() => {
    currentHunkIdxRef.current = 0;
    setDiffHunkResolved(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFileId, acceptedCode]);

  useEffect(() => {
    return () => {
      try { diffHunkStatsListenerRef.current?.dispose(); } catch { /* already disposed */ }
      diffHunkStatsListenerRef.current = null;
    };
  }, []);

  /** Inline editor unmounts when overlay opens — drop hunk-count listener tied to that instance. */
  useEffect(() => {
    if (!diffOverlayOpen) return;
    try { diffHunkStatsListenerRef.current?.dispose(); } catch { /* */ }
    diffHunkStatsListenerRef.current = null;
  }, [diffOverlayOpen]);

  /** Drop ref only — DiffCodeEditor owns Monaco dispose. */
  const disposeDiff = useCallback(() => {
    try { diffHunkStatsListenerRef.current?.dispose(); } catch { /* */ }
    diffHunkStatsListenerRef.current = null;
    diffEditorRef.current = null;
  }, [diffEditorRef]);

  /** Scroll Monaco to the hunk at `currentHunkIdxRef.current + delta`, clamped to valid range. */
  const navigateHunk = useCallback((delta: number) => {
    const next = Math.max(0, Math.min(diffHunkTotal - 1, currentHunkIdxRef.current + delta));
    currentHunkIdxRef.current = next;
    scrollToHunkRef.current?.(next);
  }, [diffHunkTotal]);

  /** Apply every proposed hunk → baseline. Clears the diff overlay and marks dirty. */
  const applyAcceptAll = useCallback(() => {
    const ed = diffEditorRef.current;
    if (!ed) {
      const accepted = acceptedCode;
      setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: accepted }));
      setContentByFileId((prev) => ({ ...prev, [activeFileId]: accepted }));
      setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
      setDiffHunkTotal(0);
      setDiffHunkResolved(0);
      setDiffOverlayOpen(false);
      onAcceptedCodeChange(null);
      return;
    }
    const origEd = ed.getOriginalEditor();
    const modEd = ed.getModifiedEditor();
    const om = origEd.getModel();
    const mm = modEd.getModel();
    if (!om || !mm) return;
    const accepted = mm.getValue();
    origEd.updateOptions({ readOnly: false });
    modEd.updateOptions({ readOnly: false });
    om.setValue(accepted);
    mm.setValue(accepted);
    origEd.updateOptions({ readOnly: true });
    modEd.updateOptions({ readOnly: true });
    setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: accepted }));
    setContentByFileId((prev) => ({ ...prev, [activeFileId]: accepted }));
    setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
    setDiffHunkTotal(0);
    setDiffHunkResolved(0);
    disposeDiff();
    setDiffOverlayOpen(false);
    onAcceptedCodeChange(null);
  }, [
    activeFileId, acceptedCode, diffEditorRef, disposeDiff,
    setContentByFileId, setOriginalByFileId, setDirtyByFileId,
    setDiffHunkResolved, setDiffHunkTotal, setDiffOverlayOpen, onAcceptedCodeChange,
  ]);

  /** Discard all proposed edits — modified reverts to the original baseline.
   *  Clears the pending-operation flag: a REJECTED delete must not leave a
   *  later Save on this (now-reverted) tab still thinking it's a delete. */
  const applyRejectAll = useCallback(() => {
    const ed = diffEditorRef.current;
    if (!ed) {
      const baseline = originalContentForDiff;
      setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: baseline }));
      setContentByFileId((prev) => ({ ...prev, [activeFileId]: baseline }));
      setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
      setDiffHunkTotal(0);
      setDiffHunkResolved(0);
      setDiffOverlayOpen(false);
      onAcceptedCodeChange(null);
      clearPendingOperation(activeFileId);
      return;
    }
    const origEd = ed.getOriginalEditor();
    const modEd = ed.getModifiedEditor();
    const om = origEd.getModel();
    const mm = modEd.getModel();
    if (!om || !mm) return;
    const baseline = om.getValue();
    modEd.updateOptions({ readOnly: false });
    mm.setValue(baseline);
    modEd.updateOptions({ readOnly: true });
    setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: baseline }));
    setContentByFileId((prev) => ({ ...prev, [activeFileId]: baseline }));
    setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
    setDiffHunkTotal(0);
    setDiffHunkResolved(0);
    disposeDiff();
    setDiffOverlayOpen(false);
    onAcceptedCodeChange(null);
    clearPendingOperation(activeFileId);
  }, [
    activeFileId, originalContentForDiff, diffEditorRef, disposeDiff,
    setContentByFileId, setOriginalByFileId, setDirtyByFileId,
    setDiffHunkResolved, setDiffHunkTotal, setDiffOverlayOpen, onAcceptedCodeChange,
  ]);

  /** Apply reviewed changes, commit to content store, and trigger save. */
  const handleApply = useCallback(() => {
    const modifiedValue =
      diffEditorRef.current?.getModifiedEditor().getValue() ?? acceptedCode;
    disposeDiff();
    setContentByFileId((prev) => ({ ...prev, [activeFileId]: modifiedValue }));
    setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
    onAcceptedCodeChange(null);
    setDiffOverlayOpen(false);
    setTimeout(() => handleSave(), 200);
  }, [
    activeFileId, acceptedCode, diffEditorRef, disposeDiff,
    setContentByFileId, setDirtyByFileId, onAcceptedCodeChange, setDiffOverlayOpen, handleSave,
  ]);

  /** Close the diff without saving — same reason as applyRejectAll: don't
   *  leave a delete's pending-operation flag behind for a later Save to
   *  misread on this tab. */
  const handleClose = useCallback(() => {
    disposeDiff();
    onAcceptedCodeChange(null);
    setDiffOverlayOpen(false);
    clearPendingOperation(activeFileId);
  }, [disposeDiff, onAcceptedCodeChange, setDiffOverlayOpen, activeFileId]);

  const lang = activeFile
    ? getMonacoLanguage(activeFile.name, activeFile.metadataType)
    : "plaintext";

  const diffEditorOptions = { ignoreTrimWhitespace: false };
  const hasTextDiff = originalContentForDiff !== acceptedCode;
  const bulkDisabled = !hasTextDiff && diffHunkTotal === 0;
  const allReviewed = diffHunkTotal === 0 || diffHunkResolved >= diffHunkTotal;

  /**
   * MULTI-AGENT path: proposed artifact with a known source artifact UUID.
   * `MultiAgentCompareView` resolves the original lazily and owns its own toolbar.
   */
  if (originalArtifactId !== undefined && originalArtifactId !== null) {
    return (
      <MultiAgentCompareView
        originalArtifactId={originalArtifactId}
        modifiedContent={acceptedCode}
        fileName={activeFile?.name}
        metadataType={activeFile?.metadataType}
        onAccept={(merged) => {
          setContentByFileId((prev) => ({ ...prev, [activeFileId]: merged }));
          setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
          onAcceptedCodeChange(null);
          setDiffOverlayOpen(false);
          setTimeout(() => handleSave(), 200);
        }}
        onReject={() => {
          onAcceptedCodeChange(null);
          setDiffOverlayOpen(false);
        }}
      />
    );
  }

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">

      {/* ── Sticky review toolbar (Cursor / VS Code style) ────────────────── */}
      {!diffOverlayOpen && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-background shrink-0 min-w-0">
          {/* Left: file name + hunk counter */}
          <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
            {activeFile && (
              <span
                className="text-xs font-mono text-muted-foreground truncate"
                title={activeFile.name}
              >
                {activeFile.name}
              </span>
            )}
            {diffHunkTotal > 0 && (
              <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums px-1.5 py-0.5 rounded bg-muted">
                {diffHunkResolved}/{diffHunkTotal}
              </span>
            )}
          </div>

          {/* Hunk navigation arrows — shown only when there are multiple hunks */}
          {diffHunkTotal > 1 && (
            <div className="flex items-center shrink-0">
              <button
                type="button"
                onClick={() => navigateHunk(-1)}
                title="Previous change"
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => navigateHunk(1)}
                title="Next change"
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Divider */}
          <div className="w-px h-3.5 bg-border shrink-0" />

          {/* Bulk actions + Apply + Close */}
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              disabled={bulkDisabled}
              onClick={applyRejectAll}
              title="Discard all proposed changes"
              className="text-[11px] px-2 py-0.5 rounded border border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              Reject all
            </button>
            <button
              type="button"
              disabled={bulkDisabled}
              onClick={applyAcceptAll}
              title="Accept every remaining change"
              className="text-[11px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              Accept all
            </button>
            <button
              type="button"
              disabled={!allReviewed}
              onClick={handleApply}
              title={
                allReviewed
                  ? "Apply reviewed changes and save"
                  : `Review all ${diffHunkTotal} change(s) before applying`
              }
              className={`text-[11px] px-2 py-0.5 rounded transition-colors ${
                allReviewed
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-muted text-muted-foreground/50 cursor-not-allowed"
              }`}
            >
              Apply
            </button>
            <button
              type="button"
              onClick={() => setDiffOverlayOpen(true)}
              title="Side-by-side comparison"
              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={handleClose}
              title="Close review"
              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ── Inline Monaco diff ─────────────────────────────────────────────── */}
      {!diffOverlayOpen && (
        <div className="flex-1 min-h-0">
          <DiffCodeEditor
            key={`diff-${activeFileId}`}
            original={originalContentForDiff}
            modified={acceptedCode}
            language={lang}
            theme={editorTheme}
            viewMode="inline"
            readOnly={true}
            editorOptions={diffEditorOptions}
            inlineActions={true}
            scrollToHunkRef={scrollToHunkRef}
            onMount={(editor) => {
              diffEditorRef.current = editor;
              try { diffHunkStatsListenerRef.current?.dispose(); } catch { /* */ }
              diffHunkStatsListenerRef.current = null;
              const syncHunkTotal = () => {
                try { setDiffHunkTotal(editor.getLineChanges()?.length ?? 0); }
                catch { setDiffHunkTotal(0); }
              };
              diffHunkStatsListenerRef.current = editor.onDidUpdateDiff(syncHunkTotal);
              syncHunkTotal();
              requestAnimationFrame(syncHunkTotal);
            }}
            onHunkAccept={(mergedBaseline) => {
              setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: mergedBaseline }));
              setContentByFileId((prev) => ({ ...prev, [activeFileId]: mergedBaseline }));
              setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
              setDiffHunkResolved((prev) => prev + 1);
            }}
            onHunkReject={(_idx, updatedModified) => {
              setContentByFileId((prev) => ({ ...prev, [activeFileId]: updatedModified }));
              setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
              setDiffHunkResolved((prev) => prev + 1);
            }}
          />
        </div>
      )}

      {/* ── Full-screen side-by-side overlay (portal to escape stacking context) */}
      {diffOverlayOpen && createPortal(
        <div className="fixed inset-0 z-[9000] bg-background/95 backdrop-blur-sm flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-3 border-b bg-muted/50 shrink-0 gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
              <span className="shrink-0 text-sm font-semibold">Comparing changes</span>
              {activeFile && (
                <span
                  className="min-w-0 truncate text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded"
                  title={activeFile.name}
                >
                  {activeFile.name}
                </span>
              )}
              <span className="shrink-0 text-xs text-muted-foreground">Original → Modified</span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const baseline =
                    diffEditorRef.current?.getOriginalEditor().getModel()?.getValue()
                    ?? originalContentForDiff;
                  setContentByFileId((prev) => ({ ...prev, [activeFileId]: baseline }));
                  setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: baseline }));
                  setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
                  setDiffHunkTotal(0);
                  setDiffHunkResolved(0);
                  disposeDiff();
                  onAcceptedCodeChange(null);
                  setDiffOverlayOpen(false);
                  clearPendingOperation(activeFileId);
                }}
                className="text-xs px-3 py-1.5 rounded border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={() => {
                  const modifiedValue =
                    diffEditorRef.current?.getModifiedEditor().getValue() ?? acceptedCode;
                  disposeDiff();
                  setContentByFileId((prev) => ({ ...prev, [activeFileId]: modifiedValue }));
                  setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: modifiedValue }));
                  setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
                  setDiffHunkTotal(0);
                  setDiffHunkResolved(0);
                  onAcceptedCodeChange(null);
                  setDiffOverlayOpen(false);
                }}
                className="text-xs px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => {
                  diffEditorRef.current = null;
                  setDiffOverlayOpen(false);
                }}
                className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Close overlay"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Side-by-side diff */}
          <div className="flex-1 min-h-0">
            <DiffCodeEditor
              key={`diff-overlay-${activeFileId}`}
              original={originalContentForDiff}
              modified={acceptedCode}
              language={lang}
              theme={editorTheme}
              viewMode="side-by-side"
              readOnly={true}
              editorOptions={diffEditorOptions}
              onMount={(editor) => { diffEditorRef.current = editor; }}
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
